<template>
  <main>
    <section class="news">
      <template v-if="postsPage && postsPage.acf.modules">
        <template v-for="(module, index) in postsPage.acf.modules">
          <template v-if="module.acf_fc_layout === 'HeroComponent'">
            <HeroComponent
              :key="index"
              :data="module"
              :index="index" />
          </template>
        </template>
      </template>
      <div
        v-if="categories.length && activeFilter"
        class="post-categories">
        <div class="container">
          <div class="categories">
            <div
              v-for="category in categories"
              :key="category.id"
              class="category"
              :class="{ active: activeFilter.id == category.id }"
              @click="handleFilter(category)">
              {{ category.name }}
            </div>
          </div>
          <div
            v-if="categories.length && activeFilter"
            class="category-dropdown"
            data-lenis-prevent>
            <div
              class="category-btn"
              :class="{ open: isOpen }"
              @click="open">
              <div class="name">
                {{ activeFilter.name }}
              </div>
              <img
                class="arrow"
                src="@/assets/img/dropdown-arrow.svg"
                alt="Drawer Arrow" />
            </div>
            <Vue3SlideUpDown v-model="isOpen">
              <ul>
                <li
                  v-for="category in categories"
                  :key="category.id"
                  class="category"
                  :class="{ active: activeFilter.id == category.id }"
                  @click="handleFilter(category)">
                  {{ category.name }}
                </li>
              </ul>
            </Vue3SlideUpDown>
          </div>
        </div>
      </div>
      <div class="posts">
        <div class="container large">
          <div
            class="posts-container"
            :class="{ loading: loading }">
            <transition-group name="posts">
              <PostItem
                v-for="post in posts"
                :key="post.id"
                :post="post" />
            </transition-group>
          </div>
          <div
            v-if="site.media_relations"
            class="relations-contact">
            <a
              class="font-smaller"
              :href="`mailto:${site.media_relations}`">
              <p>Media Relations: {{ site.media_relations }}</p>
              <div class="arrow"></div>
            </a>
          </div>
        </div>
      </div>
      <template v-if="postsPage && postsPage.acf.modules">
        <template v-for="(module, index) in postsPage.acf.modules">
          <template v-if="module.acf_fc_layout === 'PageCalloutComponent'">
            <PageCalloutComponent
              :key="index"
              :data="module"
              :index="index" />
          </template>
        </template>
      </template>
    </section>
  </main>
</template>

<script setup>
import PostItem from '@/components/template-parts/PostItem.vue'
import PageCalloutComponent from '@/components/modules/PageCalloutComponent.vue'
import HeroComponent from '@/components/modules/HeroComponent.vue'
import { inject, computed, onBeforeMount, ref } from 'vue'
import { useStore } from '../stores/main'
import { Vue3SlideUpDown } from 'vue3-slide-up-down'

const bgState = inject('bgState')

const store = useStore()
const site = ref(store.acfOptions())
const loading = ref(true)
const isOpen = ref(false)

const open = () => {
  isOpen.value = !isOpen.value
}

const props = defineProps({
  slug: {
    type: String,
    required: true,
  },
})

const pageRequest = ref({
  type: 'pages',
  slug: props.slug,
  showLoading: true,
})

const categoryRequest = ref({
  type: 'categories',
  params: {
    orderby: 'id',
  },
})
const activeFilter = ref()
const pageFeaturedImage = ref()

const posts = ref('')
const categories = computed(() => {
  return store.requestedItems(categoryRequest.value)
})
const postsPage = computed(() => {
  return store.singleBySlug(pageRequest.value)
})

const getCategories = () => {
  return store.getItems(categoryRequest.value)
}
const getPostsPage = () => {
  store.getSingleBySlug(pageRequest.value).then(() => {
    const imgRequest = {
      type: 'media',
      id: postsPage.value.featured_media,
      params: {
        _fields: 'alt_text,id,source_url',
      },
    }

    store.getSingleById(imgRequest).then(() => {
      pageFeaturedImage.value = store.singleById(imgRequest)
    })
  })
}

const handleFilter = (category) => {
  isOpen.value = false
  activeFilter.value = category

  const request = ref({
    type: 'posts',
    params: {
      categories: category.id,
      fields:
        'id,slug,title,featured_media,link,categories,excerpt,date,modified,content',
      per_page: 100,
    },
    showLoading: false,
  })

  store.getItems(request.value).then(() => {
    posts.value = store.requestedItems(request.value)
    loading.value = false
  })
}

onBeforeMount(() => {
  bgState.value.isHomepage = false
  bgState.value.class = 'light'

  store.updateDocTitle({
    parts: ['Publications & Press', store.site.name],
  })

  getPostsPage()

  getCategories().then(() => {
    const category = categories.value[0]
    handleFilter(category)
  })
})
</script>

<style lang="scss">
.news {
  .posts-enter-from {
    opacity: 0;
  }
  .posts-enter-to {
    opacity: 1;
  }

  .posts-move {
    @include transition;
  }
  .posts-enter-active {
    @include transition;
  }

  .posts-leave-active {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 0;
    @include transition;
  }

  .posts-enter,
  .posts-leave-to {
    opacity: 0;
  }
  .posts-enter {
    transform: scale(0.1);
  }

  .post-categories {
    position: relative;
    margin: 0 0 70px;

    @include tablet-landscape {
      margin: 0 0 50px;
    }

    @include mobile {
      margin: 0 0 30px;
    }

    .categories {
      display: flex;
      justify-content: space-between;
      padding: 0 0 12px;
      border-bottom: 1px solid $white;

      @include tablet-landscape(1) {
        display: none;
      }

      .category {
        cursor: pointer;
        font-size: 1.8rem;
        border-radius: 300px;
        line-height: 1;
        border-radius: 500px;
        padding: 9px 24px;
        color: $white;
        border: 2px solid transparent;
        @include transition;

        &.active {
          border: 2px solid $tech-blue;
        }
      }
    }

    .category-dropdown {
      display: none;

      @include tablet-landscape(1) {
        display: block;
      }
    }
  }

  .relations-contact {
    a {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 10px;
      color: $blue;
      background-color: $white;

      &:hover .arrow {
        transform: rotate(35deg);
      }

      .arrow {
        position: relative;
        width: 36px;
        height: 36px;
        margin-left: 10px;
        border-radius: 50%;
        transform: rotate(0deg);
        background-color: $tech-blue;
        @include transition;

        &:after {
          content: '';
          display: block;
          position: absolute;
          top: 50%;
          left: 50%;
          width: 14px;
          height: 11px;
          transform: translate(-50%, -50%);
          background-image: url('@/assets/img/arrow-out.svg');
          background-size: contain;
          background-repeat: no-repeat;
        }
      }
    }
  }

  .posts {
    padding: 0 0 85px;
  }

  .posts-container {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    &.loading {
      height: 100vh;
    }

    .post {
      width: 48.5%;
      margin-bottom: 3%;

      @include tablet-landscape(1) {
        width: 100%;
      }
    }
  }
}
</style>
